import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { stampPrizeErrors } from '../../adapters/stamp';
import PrizeFields from '../../kit/PrizeFields';
import StepPanel from '../../kit/StepPanel';
import { AddButton, RemoveButton } from '../../kit/controls';
import { SelectField } from '../../kit/fields';
import { Card, Eyebrow, Line, Words } from '../../kit/layout';
import { IWizardStepProps, MAX_STAMP_SLOTS, newPrize, StampPrize } from '../../wizardModel';

const TP = `${TOrbit}.wizard.prize`;

const SLOTS = Array.from({ length: MAX_STAMP_SLOTS }, (_, index) => index + 1);

const ORDINAL_SUFFIX: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
const ordinalRules = new Intl.PluralRules('en', { type: 'ordinal' });

export const ordinal = (n: number) => `${n}${ORDINAL_SUFFIX[ordinalRules.select(n)]}`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// 6778:20304: the add button sits under the last card, at the content's left edge.
const AddRow = styled.div`
  margin-top: 14px;
`;

/** 6778:20069 / :20311, Step 4/5: one card per prize rule, each on its own stamp. */
const StampPrizeStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const change = (index: number, prize: StampPrize) =>
    update(current => ({
      ...current,
      prizes: current.prizes.map((item, at) => (at === index ? prize : item))
    }));
  const freeSlot = SLOTS.find(slot => !state.prizes.some(prize => prize.slot === slot)) ?? 1;

  return (
    <StepPanel
      step="prizes"
      title={t(`${TOrbit}.wizard.setup.stamp`)}
      question={t(`${TP}.stampQuestion`)}
      errors={stampPrizeErrors(state.prizes, t)}
      onNext={next}
      onBack={back}
    >
      {state.prizes.map((prize, index) => (
        // Cards have no identity of their own; removing one never reorders the rest.
        <Card key={index} data-testid="orbit-wizard-prize-card">
          <CardHead>
            <Eyebrow>{t(`${TP}.rule`, { n: index + 1 })}</Eyebrow>
            {state.prizes.length > 1 ? (
              <RemoveButton
                label={t(`${TP}.remove`, { n: index + 1 })}
                onClick={() =>
                  update(current => ({
                    ...current,
                    prizes: current.prizes.filter((_, at) => at !== index)
                  }))
                }
              />
            ) : null}
          </CardHead>
          <Line>
            <Words>{t(`${TP}.slotLeadIn`)}</Words>
            <SelectField<number>
              label={t(`${TP}.slotLeadIn`)}
              value={prize.slot}
              options={SLOTS.map(slot => ({ value: slot, label: ordinal(slot) }))}
              onChange={slot => change(index, { ...prize, slot })}
            />
            <Words $muted>{t(`${TP}.slotUnit`)}</Words>
          </Line>
          <PrizeFields<StampPrize>
            name={`orbit-wizard-prize-${index}`}
            prize={prize}
            onChange={value => change(index, value)}
          />
        </Card>
      ))}
      <AddRow>
        <AddButton
          label={t(`${TP}.addRule`)}
          disabled={state.prizes.length >= MAX_STAMP_SLOTS}
          onClick={() =>
            update(current => ({
              ...current,
              prizes: [...current.prizes, { ...newPrize(), slot: freeSlot }]
            }))
          }
        />
      </AddRow>
    </StepPanel>
  );
};

export default StampPrizeStep;
